import { Component } from '@theme/component';

/**
 * Drives a visual range slider from the theme's real variant picker, for a
 * single-option "Importe" (amount) product such as a gift card sold in fixed
 * denominations.
 *
 * The real picker (rendered via `variant-main-picker`, visually hidden) stays the
 * source of truth for stock/pricing/Add to cart, since <variant-picker> already
 * listens for native 'change' events bubbling up from its own radio/select inputs
 * (see assets/variant-picker.js: `this.addEventListener('change', this.variantChanged...)`).
 * This component never talks to the cart/variant-resolution machinery directly - it
 * only ever finds the matching native input and dispatches a real 'change' event on
 * it, so every existing behavior (AJAX price/availability sync, Add to cart wiring)
 * keeps working unmodified.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} native - Wrapper around the real (hidden) variant picker.
 * @property {HTMLInputElement} range - The visible range slider.
 * @property {HTMLElement} amountLabel - Text display for the current amount.
 * @extends {Component<Refs>}
 */
export class GiftcardAmountPicker extends Component {
  requiredRefs = ['native', 'range', 'amountLabel'];

  /** @type {{ value: number, input: HTMLInputElement | HTMLOptionElement }[]} */
  #options = [];

  /** @type {number} */
  #step = 1;

  connectedCallback() {
    super.connectedCallback();
    this.#readOptionsFromNativePicker();
    this.#syncFromChecked();

    // Keep the slider in sync if the underlying selection changes some other way
    // (e.g. the theme editor re-renders the native picker).
    this.refs.native.addEventListener('change', this.#onNativeChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.native.removeEventListener('change', this.#onNativeChange);
  }

  /** @param {Event} event */
  #onNativeChange = (event) => {
    if (event.target === this.#activeInputFromSlider) return;
    this.#syncFromChecked();
  };

  /** @type {HTMLInputElement | HTMLOptionElement | null} */
  #activeInputFromSlider = null;

  /**
   * Reads every option value directly from the real (hidden) picker's own markup -
   * never hardcoded - so the slider can't drift out of sync with the product's
   * actual variants. Supports both the "buttons" (radio) and "dropdowns" (select)
   * picker styles.
   */
  #readOptionsFromNativePicker() {
    const radios = /** @type {HTMLInputElement[]} */ (
      Array.from(this.refs.native.querySelectorAll('input[type="radio"]'))
    );
    const source = radios.length
      ? radios
      : /** @type {HTMLOptionElement[]} */ (Array.from(this.refs.native.querySelectorAll('option')));

    this.#options = source
      .map((input) => ({ value: parseFloat(String(input.value).replace(/[^0-9.]/g, '')), input }))
      .filter((option) => !Number.isNaN(option.value))
      .sort((a, b) => a.value - b.value);

    if (this.#options.length < 2) return;

    const min = this.#options[0].value;
    const max = this.#options[this.#options.length - 1].value;
    this.#step = this.#options[1].value - min || 1;

    this.refs.range.min = String(min);
    this.refs.range.max = String(max);
    this.refs.range.step = String(this.#step);
  }

  /** Reflects the currently-checked/selected native option onto the slider and label. */
  #syncFromChecked() {
    const checked = this.#options.find(
      (option) =>
        (option.input instanceof HTMLInputElement && option.input.checked) ||
        (option.input instanceof HTMLOptionElement && option.input.selected)
    );
    const value = checked?.value ?? this.#options[0]?.value;
    if (value === undefined) return;

    this.refs.range.value = String(value);
    this.#renderLabel(value);
  }

  /** @param {number} value */
  #renderLabel(value) {
    const prefix = this.dataset.amountPrefix || '';
    this.refs.amountLabel.textContent = `${prefix}: PEN ${value.toFixed(2)}`;
  }

  /** Live label update while dragging; doesn't switch variants until released. */
  handleInput() {
    this.#renderLabel(parseFloat(this.refs.range.value));
  }

  /** Commits the slider's current value to the real picker. */
  handleChange() {
    this.#commit(parseFloat(this.refs.range.value));
  }

  increment() {
    this.#commit(Math.min(parseFloat(this.refs.range.max), parseFloat(this.refs.range.value) + this.#step));
  }

  decrement() {
    this.#commit(Math.max(parseFloat(this.refs.range.min), parseFloat(this.refs.range.value) - this.#step));
  }

  /**
   * Snaps to the nearest real option and - if it isn't already selected - checks it
   * and dispatches a real, bubbling 'change' event so <variant-picker> picks it up
   * exactly as if the visitor had clicked that option directly.
   * @param {number} targetValue
   */
  #commit(targetValue) {
    if (!this.#options.length) return;

    const nearest = this.#options.reduce((closest, option) =>
      Math.abs(option.value - targetValue) < Math.abs(closest.value - targetValue) ? option : closest
    );

    this.refs.range.value = String(nearest.value);
    this.#renderLabel(nearest.value);

    const alreadySelected =
      (nearest.input instanceof HTMLInputElement && nearest.input.checked) ||
      (nearest.input instanceof HTMLOptionElement && nearest.input.selected);
    if (alreadySelected) return;

    this.#activeInputFromSlider = nearest.input;

    if (nearest.input instanceof HTMLInputElement) {
      nearest.input.checked = true;
    } else {
      nearest.input.selected = true;
    }

    nearest.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('giftcard-amount-picker')) {
  customElements.define('giftcard-amount-picker', GiftcardAmountPicker);
}
